import React, { useEffect, useState, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  Tooltip,
  useMap
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import "leaflet-defaulticon-compatibility";
import "leaflet-defaulticon-compatibility/dist/leaflet-defaulticon-compatibility.css";
import L from "leaflet";

import Loader from "../../components/Loader";
import carBlip from "../../assets/blip-car-icon.webp";
import connection, { startConnection, onLocationUpdate } from "../../services/signalRClient";

const statusColors = {
  active: "green",
  idle: "orange",
  offline: "gray"
};

const FitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds.length) map.fitBounds(bounds);
  }, [bounds, map]);
  return null;
};

const calculateHeading = (lat1, lon1, lat2, lon2) => {
  const toRad = deg => deg * Math.PI / 180;
  const dLon = toRad(lon2 - lon1);
  const y = Math.sin(dLon) * Math.cos(toRad(lat2));
  const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
            Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);
  const theta = Math.atan2(y, x);
  return (theta * 180 / Math.PI + 360) % 360;
};

const LiveMap = () => {
  const [locations, setLocations] = useState([]);
  const [prevLocations, setPrevLocations] = useState({});
  const [paths, setPaths] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showClusters, setShowClusters] = useState(true);
  const [signalRStatus, setSignalRStatus] = useState("disconnected");
  const [isLoading, setIsLoading] = useState(true);
  const mapRef = useRef();

  useEffect(() => {
    startConnection();

    const fallbackTimeout = setTimeout(() => setIsLoading(false), 300);

    const updateStatus = () => {
      const state = (connection.connectionState || connection.state || "").toLowerCase();
      setSignalRStatus(state);
    };

    connection.onreconnecting(updateStatus);
    connection.onreconnected(updateStatus);
    connection.onclose(updateStatus);
    updateStatus();

    onLocationUpdate((update) => {
      const { vehicleId, latitude, longitude, status, speed = 0, timestamp = new Date() } = update;
      const prev = prevLocations[vehicleId];
      const heading = prev ? calculateHeading(prev.lat, prev.lng, latitude, longitude) : 0;

      setPrevLocations(prev => ({
        ...prev,
        [vehicleId]: { lat: latitude, lng: longitude }
      }));

      setLocations(curr => {
        const exists = curr.find(v => v.id === vehicleId);
        if (exists) {
          return curr.map(v =>
            v.id === vehicleId ? { ...v, lat: latitude, lng: longitude, status, heading, speed, timestamp } : v
          );
        }
        return [...curr, { id: vehicleId, lat: latitude, lng: longitude, status, heading, speed, timestamp }];
      });

      setLastUpdated(new Date().toLocaleTimeString());
      setIsLoading(false);
    });

    return () => clearTimeout(fallbackTimeout);
  }, []);

  const bounds = locations.map(({ lat, lng }) => [lat, lng]);

  const getIcon = (status, heading = 0) =>
    L.divIcon({
      html: `<div style="transform: rotate(${heading}deg);">
              <img src="${carBlip}" style="
                width: 40px;
                filter: drop-shadow(0 0 4px ${statusColors[status] || "blue"});
                transition: transform 0.5s ease;
              " />
            </div>`,
      className: "vehicle-icon",
      iconSize: [40, 40],
      iconAnchor: [20, 20]
    });

  const getStatusStyle = () => {
    switch (signalRStatus) {
      case "connected": return "bg-green-600 text-white";
      case "reconnecting": return "bg-yellow-500 text-white animate-pulse";
      default: return "bg-red-600 text-white animate-pulse";
    }
  };

  const zoomToVehicle = (lat, lng) => {
    if (mapRef.current) {
      mapRef.current.setView([lat, lng], 13, { animate: true });
    }
  };

  return (
    <div className="relative min-h-[600px]">
      {isLoading && (
        <div className="absolute inset-0 z-[999] flex items-center justify-center bg-white/80 dark:bg-black/60">
          <Loader />
        </div>
      )}
      <div className={`${isLoading ? "opacity-30 pointer-events-none" : ""}`}>
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="w-full lg:w-3/4 h-[500px] rounded shadow overflow-hidden border dark:border-gray-700 relative">
            <MapContainer
              center={[37.7749, -122.4194]}
              zoom={5}
              scrollWheelZoom
              className="w-full h-full"
              whenCreated={(mapInstance) => (mapRef.current = mapInstance)}
            >
              <TileLayer
                attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {showClusters ? (
                <MarkerClusterGroup>
                  {locations.map(({ id, lat, lng, status, heading, speed, timestamp }) => (
                    <Marker key={id} position={[lat, lng]} icon={getIcon(status, heading)}>
                      <Popup>
                        <strong>{id}</strong><br />
                        Status: {status}<br />
                        Heading: {heading?.toFixed(1)}°<br />
                        Speed: {speed} km/h<br />
                        Lat: {lat.toFixed(4)}<br />
                        Lng: {lng.toFixed(4)}<br />
                        Time: {new Date(timestamp).toLocaleTimeString()}
                      </Popup>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
              ) : (
                locations.map(({ id, lat, lng, status, heading, speed, timestamp }) => (
                  <Marker key={id} position={[lat, lng]} icon={getIcon(status, heading)}>
                    <Popup>
                      <strong>{id}</strong><br />
                      Status: {status}<br />
                      Heading: {heading?.toFixed(1)}°<br />
                      Speed: {speed} km/h<br />
                      Lat: {lat.toFixed(4)}<br />
                      Lng: {lng.toFixed(4)}<br />
                      Time: {new Date(timestamp).toLocaleTimeString()}
                    </Popup>
                  </Marker>
                ))
              )}

              {Object.entries(paths).map(([id, coords]) => (
                <Polyline key={id} positions={coords} color="orange" weight={3}>
                  <Tooltip>{id} route</Tooltip>
                </Polyline>
              ))}

              <FitBounds bounds={bounds} />
            </MapContainer>

            <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs shadow z-[999] ${getStatusStyle()}`}>
              {signalRStatus === "connected" ? "🟢" : signalRStatus === "reconnecting" ? "🟠" : "🔴"} {signalRStatus}
            </div>
          </div>

          <div className="w-full lg:w-1/4 space-y-4">
            <div className="bg-white dark:bg-gray-800 rounded shadow p-4">
              <h2 className="text-lg font-semibold mb-2">Vehicles</h2>
              <label className="text-sm flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={showClusters}
                  onChange={(e) => setShowClusters(e.target.checked)}
                />
                Enable Clustering
              </label>
              <ul className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                {locations.map(({ id, lat, lng, status }) => (
                  <li
                    key={id}
                    className="flex items-center justify-between text-sm cursor-pointer hover:text-orange-500"
                    onClick={() => zoomToVehicle(lat, lng)}
                  >
                    <span>{id}</span>
                    <span
                      className="ml-2 w-3 h-3 rounded-full"
                      style={{ backgroundColor: statusColors[status] }}
                    ></span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 text-right">
              Last updated: {lastUpdated || "—"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveMap;
